import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { selectAll, spendFeatureKey, SpendState } from './spend.reducer';
import {
  ICommittedItem,
  ICommittedItemExtended,
  ILastSpendStoreUpdate,
  ILineItem,
  ILineItemExtended,
  ILineItemsTotal,
  IMonthlySpendData,
  ISubitemExtended,
  SpendStoreUpdateTypes,
} from './spend.interfaces';
import {
  defaultMonthlyData,
  defaultMonthlyDisable,
  SPEND_TYPES,
} from '@app/framework/constants/spend.constants';
import { FiscalService } from '@app/services/fiscal.service';
import {
  getProjectTotalPerItem,
  updateBudgetWithSubitemBudget,
  getYearlyTotalPerItem,
  getMonthlyForecastForYear,
  getMonthlyDataForYear,
} from '@app/framework/helpers/spend.helper';

export const spendFeatureSelector = createFeatureSelector<SpendState>(spendFeatureKey);

export const selectAllLineItems = createSelector(spendFeatureSelector, selectAll);

export const selectLineItemsAndSubitems = createSelector(selectAllLineItems, (lineItems) => {
  return lineItems.flatMap((item) => [item, ...item.subitems]);
});

export const getSelectedYear = createSelector(spendFeatureSelector, (state) => state.selectedYear);

export const getSpendDistributionState = createSelector(
  spendFeatureSelector,
  (state) => state.spendDistributionState,
);

export const getModifiedLineItems = createSelector(
  spendFeatureSelector,
  (state) =>
    ({
      modifiedLineItems: state.modifiedLineItems,
      newLineItems: state.newLineItems,
      deletedLineItems: state.deletedLineItems,
    }) as Partial<SpendState>,
);

export const getProjectStartDate = createSelector(
  spendFeatureSelector,
  (state) => state.projectStartDate,
);
export const getSelectedSpendType = createSelector(
  spendFeatureSelector,
  (state) => state.selectedSpendType,
);
export const getIsLoading = createSelector(spendFeatureSelector, (state) => state.isLoading);

export const getLineItemById = (id: number, searchSubitems = false) =>
  createSelector(selectAllLineItems, (items) => {
    if (searchSubitems) {
      return items.flatMap((item) => item.subitems).find((item) => item.id === id);
    }
    return items.find((item) => item.id === id);
  });

export const projectHasBudget = createSelector(selectAllLineItems, (lineItems) => {
  return lineItems.length > 0;
});

export const selectSpendShowZeroLines = createSelector(spendFeatureSelector, (state) => state.showZeroLines);

export const selectLineItemsExtended = createSelector(
  selectAllLineItems,
  getSelectedYear,
  selectSpendShowZeroLines,
  (lineItems, selectedYear, showZeroLines) => {
    const lineItemsSorted = lineItems.toSorted((a, b) => a.row_number - b.row_number);
    const lineItemsExtended =  lineItemsSorted.map((item) => {
      const hasSubItems = Array.isArray(item.subitems) && item.subitems.length > 0;
      const lineItem = getLineItemExtended(item, selectedYear, hasSubItems);
      lineItem.committed_items = lineItem.committed_items.toSorted(
        (a, b) => a.committed_row_number - b.committed_row_number,
      );
      if (item.subitems) {
        lineItem.subitems = item.subitems
          .map((subitem) => {
            return getLineItemExtended(subitem, selectedYear, false);
          })
          .toSorted((a, b) => a.row_number - b.row_number)
          .filter(item => showZeroLines || item.project_total !== 0);
      }
      return lineItem;
    });

    return lineItemsExtended.filter(item => showZeroLines || item.project_total !== 0);
  },
);

export const selectPrimeLinesExtended = createSelector(
  selectAllLineItems,
  getSelectedYear,
  selectSpendShowZeroLines,
  (lineItems, selectedYear, showZeroLines) => {
    const primeLines = lineItems.filter((item) => item.used_in_pc);
    const lineItemsSorted = primeLines.toSorted((a, b) => a.row_number - b.row_number);
    const lineItemsExtended =  lineItemsSorted.map((item) => {
      const primeLineItem = getPrimeLineExtended(item, selectedYear, true);
      if (primeLineItem.subitems) {
        primeLineItem.subitems = item.subitems
          .filter((subitem) => subitem.used_in_pc)
          .map((subitem) => getPrimeLineExtended(subitem, selectedYear, false))
          .toSorted((a, b) => a.row_number - b.row_number)
          .filter(item => showZeroLines || item.project_total !== 0);
      }
      return primeLineItem;
    });

    return lineItemsExtended.filter((item) => showZeroLines || item.project_total !== 0);
  },
);

export const selectLineItemsAndSubitemsExtended = createSelector(
  selectLineItemsExtended,
  (lineItems) => {
    return lineItems.flatMap((item) => {
      return [item, ...item.subitems];
    });
  },
);

export const selectAllCommittedItems = createSelector(
  selectLineItemsAndSubitemsExtended,
  (items) => {
    return items
      .flatMap((item) =>
        item.committed_items.map((committedItem) => ({ ...committedItem, lineItem: item })),
      )
      .sort((a, b) => a.row_number - b.row_number);
  },
);

export const getForecastProjectTotalPerItem = (item: ILineItem | ICommittedItem) => {
  return item.budget
    ?.map((bud) => bud?.monthly_forecast ?? { ...defaultMonthlyData })
    ?.reduce(
      (total, monthlySpendData) =>
        total +
        Object.values(monthlySpendData).reduce(
          (monthlyTotal: number, value: number) => monthlyTotal + value,
          0,
        ),
      0,
    );
};

const getCommittedItemExtended = (selectedYear: number) => (committedItem: ICommittedItem) => {
  const commitedMonthlyData: IMonthlySpendData = getMonthlyForecastForYear(
    committedItem.budget,
    selectedYear,
  );
  return {
    ...committedItem,
    monthly_data: commitedMonthlyData,
    monthly_disable: { ...defaultMonthlyDisable },
    year_total: getYearlyTotalPerItem(commitedMonthlyData),
    project_total: getForecastProjectTotalPerItem(committedItem),
  };
};

const getLineItemExtended = (item: ILineItem, selectedYear: number, hasSubitems = false) => {
  let budget = item.budget;

  // if the item is a parent, it doesn't have a budget, it's only calculated based on its subitems
  if (hasSubitems) {
    budget = []; // reset the budget to forget any budget accidentally set on the parent item
    for (const subitem of item.subitems) {
      for (const yearlyBudget of subitem.budget) {
        budget = updateBudgetWithSubitemBudget(yearlyBudget, budget);
      }
    }
  }

  const monthlyData: IMonthlySpendData = getMonthlyDataForYear(budget, selectedYear);
  const yearTotal = getYearlyTotalPerItem(monthlyData);
  const projectTotal = getProjectTotalPerItem(budget);
  const lineItem: ILineItemExtended = {
    ...item,
    budget,
    year_total: yearTotal,
    monthly_data: monthlyData,
    monthly_disable: { ...defaultMonthlyDisable },
    prime_monthly_disable: { ...defaultMonthlyDisable },
    project_total: projectTotal,
    committed_items: item.committed_items.map(getCommittedItemExtended(selectedYear)),
    subitems: [], // will be set directly in the selector
  };

  return lineItem;
};

const getPrimeLineExtended = (item: ILineItem, selectedYear: number, isParent) => {
  // if the item is a parent, we need to calculate the budget based on its subitems
  let budget = item.budget;
  if (isParent) {
    budget = [];
    for (const subitem of item.subitems) {
      for (const yearlyBudget of subitem.budget) {
        budget = updateBudgetWithSubitemBudget(yearlyBudget, budget);
      }
    }
  }

  const monthlyData: IMonthlySpendData = getMonthlyDataForYear(
    budget,
    selectedYear,
    'monthly_prime',
  );
  const yearTotal = getYearlyTotalPerItem(monthlyData);
  const projectTotal = getProjectTotalPerItem(budget, 'monthly_prime');
  const lineItem: ILineItemExtended = {
    ...item,
    budget,
    year_total: yearTotal,
    monthly_data: monthlyData,
    monthly_disable: { ...defaultMonthlyDisable },
    prime_monthly_disable: { ...defaultMonthlyDisable },
    project_total: projectTotal,
    committed_items: [],
    subitems: [], // will be set directly in the selector
  };

  return lineItem;
};

const getTotalsForOneItem = (
  item: ILineItemExtended | ICommittedItemExtended,
  total: ILineItemsTotal,
) => {
  total.project_total += item.project_total ? item.project_total : 0;
  total.year_total += item.year_total ? item.year_total : 0;
  Object.keys(total.monthly_data).forEach((monthKey) => {
    total.monthly_data[monthKey] += item.monthly_data?.[monthKey] ? item.monthly_data[monthKey] : 0;
  });

  return total;
};

export const selectLineItemTotals = createSelector(
  selectLineItemsExtended,
  selectPrimeLinesExtended,
  getSelectedSpendType,
  (lineItems, primeLines, selectedSpendType) => {
    const total: ILineItemsTotal = {
      project_total: 0,
      year_total: 0,
      monthly_data: { ...defaultMonthlyData },
    };

    if (
      selectedSpendType === SPEND_TYPES.BUDGET ||
      selectedSpendType === SPEND_TYPES.BUDGET_PARENT_ONLY ||
      selectedSpendType === SPEND_TYPES.BUDGET_PARENT_CHILD
    ) {
      return lineItems.reduce((acc, item) => getTotalsForOneItem(item, acc), total);
    }

    if (selectedSpendType === SPEND_TYPES.PRIME_CONTRACT_PARENT_ONLY || selectedSpendType === SPEND_TYPES.PRIME_CONTRACT_PARENT_CHILD) {
      return primeLines.reduce((acc, item) => getTotalsForOneItem(item, acc), total);
    }

    if (selectedSpendType === SPEND_TYPES.COMMITMENTS) {
      return lineItems.reduce((allCommittedTotals, lineItem) => {
        // first calculate the total for the parent lines
        const lineItemsCommittedTotal = lineItem.committed_items.reduce(
          (lineItemCommittedTotals, committedItem) => {
            return getTotalsForOneItem(committedItem, lineItemCommittedTotals);
          },
          allCommittedTotals,
        );

        // then add the subitems' committed items' totals
        return lineItem.subitems.reduce((subitemTotals, subitem) => {
          return subitem.committed_items.reduce((subitemCommittedTotals, committedItem) => {
            return getTotalsForOneItem(committedItem, subitemCommittedTotals);
          }, subitemTotals);
        }, lineItemsCommittedTotal);
      }, total);
    }

    return total;
  },
);

// return the newly added years which are not be present in line item budget data
export const getNewYears = createSelector(spendFeatureSelector, (state) => state.newYears);

// returns all the years that can be selected in the dropdown
export const getSelectableYears = createSelector(
  selectAllLineItems,
  getNewYears,
  (lineItems, newYears) => {
    return getAllPossibleYearsFunction(lineItems, newYears);
  },
);

const getLastStoreUpdate = createSelector(spendFeatureSelector, (state) => state.lastStoreUpdate);

export const getNextOrder = createSelector(selectAllLineItems, (lineItems): number => {
  if (!lineItems.length) {
    return 1;
  }

  const maxOrder = Math.max(...lineItems.map((item) => item?.row_number ?? 0));
  return maxOrder + 1;
});

export const getStoreUpdates = createSelector(
  selectLineItemsAndSubitemsExtended, // todo [2024-10-14]: it should not recalculate everything
  getLastStoreUpdate,
  (extendedLineItems, update): ILastSpendStoreUpdate => {
    let lineItems: ILineItemExtended[] = [];
    if (
      update.type === SpendStoreUpdateTypes.SET_ALL ||
      update.type === SpendStoreUpdateTypes.FILTER_CHANGE
    ) {
      lineItems = extendedLineItems.filter((it) => !(it as ISubitemExtended).is_subitem);
    } else if (
      update.type === SpendStoreUpdateTypes.DELETE ||
      update.type === SpendStoreUpdateTypes.NONE
    ) {
      lineItems = [];
    } else if (update.type === SpendStoreUpdateTypes.UPDATE_SUBITEM) {
      const item = extendedLineItems.find((lineItem) => lineItem.id === update.lineId);
      if (!item) {
        console.error('getStoreUpdates: could not find line item with id', update.lineId);
        return {
          lastStoreUpdate: update,
          lineItems: [],
        };
      }
      const subitem = item.subitems.find((subitem) => subitem.id === update.subitemId);
      lineItems.push(item, subitem);
    } else {
      const item = extendedLineItems.find((lineItem) => lineItem.id === update.lineId);
      lineItems.push(item);
    }

    return {
      lastStoreUpdate: update,
      lineItems,
    };
  },
);

// mixes together the years from line items and years added added by the user
// returns an ascending continuous array (no gaps between years)
export const getAllPossibleYearsFunction = (lineItems: ILineItem[], newYears: number[]) => {
  const possibleYears = getLineItemPossibleYearsFunction(lineItems);
  if (possibleYears?.[0] === undefined) {
    return [];
  }
  const allYears = possibleYears.concat(newYears);
  const minYear = allYears.reduce((acc, value) => (acc > value ? value : acc), allYears[0]);
  const maxYear = allYears.reduce((acc, value) => (acc < value ? value : acc), allYears[0]);
  return new Array(maxYear - minYear + 1).fill(0).map((__, index) => minYear + index);
};

// returns the years present in lineItem.budget
export const getLineItemPossibleYearsFunction = (lineItems: ILineItem[]) => {
  if (lineItems.length === 0) {
    return [FiscalService.fiscalYear];
  }
  const allYears = lineItems
    .map((item) => item.budget.map((budget) => budget.year))
    .reduce((acc, curr) => {
      acc.push(...curr);
      return acc;
    }, []);

  return [...new Set(allYears)].sort((a, b) => a - b);
};

/**
 * @deprecated - we use the hasCommitments selector instead for disabling things
 */
export const getDisableBasedOnTemplate = createSelector(spendFeatureSelector, (state) => {
  return state.hasTemplates;
});

export const getSpendLineItemSummary = createSelector(spendFeatureSelector, (state) => {
  return state.spendLineItemSummary;
});

export const selectHasCommitments = createSelector(selectLineItemsAndSubitems, (lineItems) => {
  return lineItems.some((lineItem) => !!lineItem.commitment_start_date);
});

/**
 * returns true if there are any line items with a prime budget having any value > 0
 */
export const selectHasPrimeLines = createSelector(selectLineItemsAndSubitems, (lineItems) => {
  return lineItems.some((lineItem) => lineItem.used_in_pc);
});

export const selectSpendViews = (isManager: boolean) =>
  createSelector(
    spendFeatureSelector,
    selectHasCommitments,
    selectHasPrimeLines,
    (state, hasCommitments, hasPrimeLines) => {
      let views: SPEND_TYPES[];
      if (isManager) {
        views = [SPEND_TYPES.BUDGET];
        if (hasCommitments) {
          views.push(SPEND_TYPES.BUDGET_COMMITMENTS);
          views.push(SPEND_TYPES.COMMITMENTS);
        }
      } else {
        views = [SPEND_TYPES.BUDGET_PARENT_ONLY, SPEND_TYPES.BUDGET_PARENT_CHILD];
        if (hasCommitments) {
          views.push(SPEND_TYPES.BUDGET_COMMITMENTS_GC);
          views.push(SPEND_TYPES.COMMITMENTS);
        }
        if (hasPrimeLines) {
          views.push(
            SPEND_TYPES.PRIME_CONTRACT_PARENT_ONLY,
            SPEND_TYPES.PRIME_CONTRACT_PARENT_CHILD,
          );
        }
      }
      return views;
    },
  );

export const selectAllSpendSubItems = createSelector(selectAllLineItems, (lineItems) => {
  const allSubitems = lineItems.flatMap((item) => item.subitems);
  return allSubitems.map((item) => {
    return {
      id: item.id,
      name: item.name,
      division: item.division,
      cost_type: item.cost_type,
      value: item.original_budget_total,
    };
  });
});
